import { SearchIcon, XIcon } from "@heroicons/react/outline";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { useCallback, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import LoadingSpinner from "../LoadingSpinner";

import { Button } from "./button";
import { Command, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { OverflowDisplayTooltip } from "./Tooltip/OverflowDisplayTooltip";

import { cn } from "@/lib/utils";

const VirtualizedList: React.FC<{
  virtualizedList: ListItem[];
  isSelected: (item: unknown) => boolean;
  onSelect: (item: unknown) => void;
  size: SearchableDropdownSize;
}> = ({ virtualizedList, isSelected, onSelect, size }) => (
  <div className="h-64">
    <Virtuoso
      style={{ height: "100%" }}
      data={virtualizedList}
      overscan={5}
      // eslint-disable-next-line react/no-unstable-nested-components
      itemContent={(_index, listItem) => {
        if (listItem.type === "category") {
          return (
            <div
              className={cn(
                "font-semibold uppercase tracking-wider text-muted-foreground",
                size === "sm" ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs",
              )}
            >
              {listItem.categoryLabel}
            </div>
          );
        }

        if (listItem.type === "divider") {
          return <div className="mx-2 my-1 h-px bg-border" />;
        }

        if (listItem.type === "item") {
          const item = listItem.data;
          const selected = isSelected(item);

          return (
            <SearchableDropdownItem
              item={item}
              selected={selected}
              onSelect={onSelect}
              size={size}
            />
          );
        }

        return null;
      }}
    />
  </div>
);

const RegularList: React.FC<{
  virtualizedList: ListItem[];
  isSelected: (item: unknown) => boolean;
  onSelect: (item: unknown) => void;
  size: SearchableDropdownSize;
}> = ({ virtualizedList, isSelected, onSelect, size }) => (
  <div className="max-h-64 overflow-y-auto">
    {virtualizedList.map((listItem, index) => {
      if (listItem.type === "category") {
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`category-${index}`}
            className={cn(
              "font-semibold uppercase tracking-wider text-muted-foreground",
              size === "sm" ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs",
            )}
          >
            {listItem.categoryLabel}
          </div>
        );
      }

      if (listItem.type === "divider") {
        // eslint-disable-next-line react/no-array-index-key
        return <div key={`divider-${index}`} className="mx-2 my-1 h-px bg-border" />;
      }

      if (listItem.type === "item") {
        const item = listItem.data;
        const selected = isSelected(item);

        return (
          <SearchableDropdownItem
            key={item.id}
            item={item}
            selected={selected}
            onSelect={onSelect}
            size={size}
          />
        );
      }

      return null;
    })}
  </div>
);

const DropdownContent: React.FC<{
  hasItems: boolean;
  shouldUseVirtualization: boolean;
  emptyMessage: string;
  virtualizedList: ListItem[];
  isSelected: (item: unknown) => boolean;
  onSelect: (item: unknown) => void;
  isLoading?: boolean;
  size: SearchableDropdownSize;
}> = ({
  hasItems,
  shouldUseVirtualization,
  emptyMessage,
  virtualizedList,
  isSelected,
  onSelect,
  isLoading = false,
  size,
}) => {
  if (isLoading && !hasItems) {
    return (
      <div className="flex items-center justify-center py-6">
        <LoadingSpinner size={4} />
      </div>
    );
  }

  if (!hasItems) {
    return (
      <div
        className={cn(
          "px-4 py-6 text-center text-muted-foreground",
          size === "sm" ? "text-xs" : "text-sm",
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  if (shouldUseVirtualization) {
    return (
      <VirtualizedList
        virtualizedList={virtualizedList}
        isSelected={isSelected}
        onSelect={onSelect}
        size={size}
      />
    );
  }

  return (
    <RegularList
      virtualizedList={virtualizedList}
      isSelected={isSelected}
      onSelect={onSelect}
      size={size}
    />
  );
};

interface Category {
  label: string;
  items: {
    id: string;
    label: string;
    description?: string;
    rightLabel?: string;
    searchKeywords?: string;
    parentLabel?: string;
    icon?: React.ComponentType<{ className?: string }>;
    onClick?: () => void;
  }[];
}

const getDropdownItemDisplayLabel = (
  item?: {
    label?: string;
    parentLabel?: string;
  } | null,
) => {
  if (!item?.label) {
    return undefined;
  }
  if (item.parentLabel) {
    return `${item.parentLabel}.${item.label}`;
  }
  return item.label;
};

interface SearchableDropdownProps {
  categories: Category[];
  selectedItem?: {
    id?: string;
    label: string;
    description?: string;
    rightLabel?: string;
    searchKeywords?: string;
    parentLabel?: string;
    icon?: React.ComponentType<{ className?: string }>;
    value?: string;
  };
  readonly?: boolean;
  removeable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect?: (item: any) => void;
  className?: string;
  triggerClassName?: string;
  customTrigger?: React.ReactNode;
  emptyMessage?: string;
  placeholder?: string;
  forceOpen?: boolean;
  /** When true, search filters via `onSearchChange` instead of locally. */
  remoteSearch?: boolean;
  onSearchChange?: (query: string) => void;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  alwaysShowSearch?: boolean;
  size?: SearchableDropdownSize;
}

type SearchableDropdownSize = "md" | "sm";

interface ListItem {
  type: "category" | "item" | "divider";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  categoryLabel?: string;
}

interface SearchableDropdownItemProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any;
  selected: boolean;
  onSelect: (item: unknown) => void;
  size: SearchableDropdownSize;
}

function SearchableDropdownItem({ item, selected, onSelect, size }: SearchableDropdownItemProps) {
  const ItemIcon = item.icon;
  const hasRightLabel = Boolean(item.rightLabel);

  return (
    <CommandItem
      value={item.id}
      onSelect={() => onSelect(item)}
      className={cn(
        "relative flex cursor-pointer select-none items-start gap-0 rounded-sm outline-none transition-colors",
        size === "sm" ? "mx-1.5 px-2 py-1.5 text-xs" : "mx-2 px-2 py-2 text-sm",
        hasRightLabel ? "pr-28" : "pr-8",
        selected && "bg-gray-100",
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5 pr-3">
        {ItemIcon && (
          <ItemIcon
            className={cn(
              "flex-shrink-0 text-muted-foreground",
              size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
            )}
          />
        )}
        <div className="min-w-0 flex-1">
          <OverflowDisplayTooltip content={item.label} className="font-medium">
            {item.label}
          </OverflowDisplayTooltip>
          {item.description && (
            <OverflowDisplayTooltip
              content={item.description}
              className="mt-0.5 text-xs text-muted-foreground"
            >
              {item.description}
            </OverflowDisplayTooltip>
          )}
        </div>
      </div>
      {item.rightLabel && (
        <span className="absolute right-3 top-2 rounded bg-muted px-1.5 py-0.5 text-xs font-medium leading-none text-muted-foreground">
          {item.rightLabel}
        </span>
      )}
      {selected && !hasRightLabel && (
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          <CheckIcon className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </span>
      )}
    </CommandItem>
  );
}

export function SearchableDropdown({
  categories,
  selectedItem,
  readonly = false,
  removeable = false,
  onSelect = () => undefined,
  className = "w-full",
  triggerClassName = "",
  customTrigger,
  emptyMessage = "No results found.",
  placeholder = "Select...",
  forceOpen = false,
  remoteSearch = false,
  onSearchChange,
  onOpenChange,
  isLoading = false,
  searchPlaceholder = "Search...",
  alwaysShowSearch = false,
  size = "md",
}: SearchableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const commandRef = useRef<HTMLDivElement>(null);

  const totalItemsCount = categories.reduce((count, category) => count + category.items.length, 0);

  const showSearch = remoteSearch || alwaysShowSearch || totalItemsCount > 5;

  const allItems = useMemo(
    () =>
      categories.flatMap(category =>
        category.items.map(item => ({ ...item, category: category.label })),
      ),
    [categories],
  );

  const filteredItems = useMemo(() => {
    if (remoteSearch || !showSearch || !searchQuery.trim()) {
      return allItems;
    }
    const normalizedQuery = searchQuery.toLowerCase();
    return allItems.filter(item => {
      if (item.label.toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      if (item.description?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      if (item.searchKeywords?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      // Match the category/group label too so searching "Trace A" surfaces
      // nested fields grouped under that parent, not only the parent column.
      if (item.category?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      return false;
    });
  }, [allItems, remoteSearch, searchQuery, showSearch]);

  const virtualizedList = useMemo(() => {
    const list: ListItem[] = [];

    if (filteredItems.length === 0) {
      return list;
    }

    categories.forEach((category, categoryIndex) => {
      const categoryItems = filteredItems.filter(item => item.category === category.label);

      if (categoryItems.length === 0) {
        return;
      }

      list.push({ type: "category", categoryLabel: category.label });

      categoryItems.forEach(item => {
        list.push({ type: "item", data: item });
      });

      const hasMoreCategoriesWithItems = categories
        .slice(categoryIndex + 1)
        .some(cat => filteredItems.some(item => item.category === cat.label));

      if (hasMoreCategoriesWithItems) {
        list.push({ type: "divider" });
      }
    });

    return list;
  }, [categories, filteredItems]);

  const handleSearchQueryChange = useCallback(
    (nextQuery: string) => {
      setSearchQuery(nextQuery);
      onSearchChange?.(nextQuery);
    },
    [onSearchChange],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      onOpenChange?.(nextOpen);
      if (!nextOpen) {
        setSearchQuery("");
        onSearchChange?.("");
      }
    },
    [onOpenChange, onSearchChange],
  );

  const handleSelect = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any) => {
      onSelect(item);
      item.onClick?.();
      handleOpenChange(false);
    },
    [handleOpenChange, onSelect],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(undefined);
    },
    [onSelect],
  );

  const isSelected = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any) => {
      if (!selectedItem) {
        return false;
      }

      if (selectedItem.id && item.id) {
        return selectedItem.id === item.id;
      }

      if (selectedItem.value && item.value) {
        return selectedItem.value === item.value;
      }

      return selectedItem.label === item.label;
    },
    [selectedItem],
  );

  const SelectedIcon = selectedItem?.icon;
  const selectedDisplayLabel = getDropdownItemDisplayLabel(selectedItem);

  const defaultTrigger = (
    <button
      type="button"
      role="combobox"
      aria-expanded={isOpen}
      disabled={readonly}
      className={cn(
        "flex w-full items-center justify-between rounded-md border border-input bg-transparent shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground",
        size === "sm" ? "h-8 px-2.5 py-1.5 text-xs" : "h-9 px-3 py-2 text-sm",
        triggerClassName,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {SelectedIcon && (
          <SelectedIcon
            className={cn(
              "flex-shrink-0 text-muted-foreground",
              size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
            )}
          />
        )}
        <OverflowDisplayTooltip
          key={selectedDisplayLabel ?? "placeholder"}
          content={selectedDisplayLabel ?? placeholder}
          className={cn(selectedDisplayLabel ? "" : "text-muted-foreground")}
          shouldShow={Boolean(selectedDisplayLabel)}
        >
          {selectedDisplayLabel || placeholder}
        </OverflowDisplayTooltip>
      </div>
      <div className="flex items-center gap-1">
        {removeable && selectedItem && !readonly && (
          <Button
            type="button"
            variant="ghost"
            onPointerDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={handleRemove}
            size="tinyIcon"
            className="h-4 w-4"
          >
            <XIcon className="h-3 w-3" />
          </Button>
        )}
        <CaretSortIcon
          className={cn("flex-shrink-0 opacity-50", size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4")}
        />
      </div>
    </button>
  );

  // Guided/tour presentation: render the real options list (real `categories`
  // + `selectedItem` data) in normal flow, independent of any popover
  // open-state machinery and free of any ancestor overflow clipping.
  if (forceOpen) {
    const staticTrigger = (
      <div
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm",
          triggerClassName,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {SelectedIcon && <SelectedIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
          <OverflowDisplayTooltip
            key={selectedDisplayLabel ?? "placeholder"}
            content={selectedDisplayLabel ?? placeholder}
            className={cn(selectedDisplayLabel ? "" : "text-muted-foreground")}
            shouldShow={Boolean(selectedDisplayLabel)}
          >
            {selectedDisplayLabel || placeholder}
          </OverflowDisplayTooltip>
        </div>
        <CaretSortIcon className="h-4 w-4 flex-shrink-0 opacity-50" />
      </div>
    );

    return (
      <div className="relative">
        {customTrigger ?? staticTrigger}
        <div className="relative z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover pb-2 text-popover-foreground shadow-lg">
          {filteredItems.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {categories.map(category => {
                if (category.items.length === 0) {
                  return null;
                }
                return (
                  <div key={category.label}>
                    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {category.label}
                    </div>
                    {category.items.map(item => {
                      const ItemIcon = item.icon;
                      const selected = isSelected(item);
                      const hasRightLabel = Boolean(item.rightLabel);
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            "relative mx-2 flex select-none items-start rounded-sm px-2 py-2 text-sm",
                            hasRightLabel ? "pr-28" : "pr-8",
                            selected ? "bg-gray-100 text-foreground" : "text-foreground",
                          )}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-2.5 pr-3">
                            {ItemIcon && (
                              <ItemIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                            )}
                            <div className="min-w-0 flex-1">
                              <OverflowDisplayTooltip content={item.label} className="font-medium">
                                {item.label}
                              </OverflowDisplayTooltip>
                              {item.description && (
                                <OverflowDisplayTooltip
                                  content={item.description}
                                  className="mt-0.5 text-xs text-muted-foreground"
                                >
                                  {item.description}
                                </OverflowDisplayTooltip>
                              )}
                            </div>
                          </div>
                          {item.rightLabel && (
                            <span className="absolute right-3 top-2 rounded bg-muted px-1.5 py-0.5 text-xs font-medium leading-none text-muted-foreground">
                              {item.rightLabel}
                            </span>
                          )}
                          {selected && !hasRightLabel && (
                            <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {customTrigger ? (
          <button
            type="button"
            disabled={readonly}
            className={cn("text-left focus:outline-none", triggerClassName || "w-full")}
          >
            {customTrigger}
          </button>
        ) : (
          defaultTrigger
        )}
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        avoidCollisions
        collisionPadding={12}
        sticky="always"
        className={cn(
          "mx-0 w-[--radix-popover-trigger-width] overflow-hidden p-0 pb-2",
          // `w-full` meant "match the trigger" in the old absolutely-positioned
          // panel; the trigger-width default above already covers that.
          className === "w-full" ? undefined : className,
        )}
        style={{
          maxWidth:
            "min(var(--radix-popover-trigger-width), var(--radix-popover-content-available-width), calc(100vw - 24px))",
          maxHeight:
            "min(20rem, calc(100dvh - 24px), var(--radix-popover-content-available-height))",
        }}
        onWheel={event => event.stopPropagation()}
        onOpenAutoFocus={event => {
          // Focus the search input when present, otherwise the cmdk root so
          // arrow-key navigation works immediately.
          event.preventDefault();
          (searchInputRef.current ?? commandRef.current)?.focus();
        }}
      >
        <Command ref={commandRef} tabIndex={-1} shouldFilter={false} className="outline-none">
          {showSearch && (
            <div className="border-b bg-popover/95 px-3 py-2 backdrop-blur-sm">
              <div className="relative">
                <SearchIcon
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 transform text-gray-400",
                    size === "sm" ? "left-2.5 h-3.5 w-3.5" : "left-3 h-4 w-4",
                  )}
                />
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={e => handleSearchQueryChange(e.target.value)}
                  className={cn(
                    "w-full rounded-md border-0 bg-gray-100 text-xs shadow-none focus:border-0 focus:ring-0",
                    size === "sm" ? "h-7 pl-8 pr-3" : "h-8 pl-10 pr-4",
                  )}
                  placeholder={searchPlaceholder}
                />
              </div>
            </div>
          )}

          <CommandList
            className="overflow-y-auto"
            style={{
              maxHeight: "min(18rem, calc(var(--radix-popover-content-available-height) - 3.5rem))",
            }}
          >
            <DropdownContent
              hasItems={filteredItems.length > 0}
              shouldUseVirtualization={virtualizedList.length > 50}
              emptyMessage={emptyMessage}
              virtualizedList={virtualizedList}
              isSelected={isSelected}
              onSelect={handleSelect}
              isLoading={isLoading}
              size={size}
            />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
